import React, { useEffect, useRef, useState } from 'react';
import { select, selectAll } from 'd3-selection';
import { scaleLinear } from 'd3-scale';
import { max } from 'd3-array';
import { line, curveLinear } from 'd3-shape';
import TravelGraphsHeading from '../TravelGraphsHeading';
import Styles from './SurfHeightGraphTravel.module.scss';

interface Data {
  id: string;
  color?: string;
  data: {
    month?: string;
    x: number;
    y: number;
  }[];
  serieId?: string;
}

interface Props {
  activeIndex: number;
  data: Data[];
  mobile: boolean;
  surfHeightUnit?: string;
}

interface Slice {
  points: {
    activeIndex: number;
    id: string;
    mobile: boolean;
    data: {
      x: number;
      y: number;
      yFormatted: number;
    };
    serieId?: string;
  }[];
}

const SurfHeightGraphTravel: React.FC<Props> = ({
  data,
  mobile,
  activeIndex,
  surfHeightUnit = 'FT',
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 1119, height: 200 });

  // Update dimensions when container size changes
  useEffect(() => {
    if (!containerRef.current) return undefined;

    const updateDimensions = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: rect.width || 1119,
          height: 200, // Keep height fixed or make it responsive too
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => updateDimensions());
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!svgRef.current || mobile || !dimensions.width) return undefined;

    // More robust data validation
    if (
      !data ||
      data.length < 3 ||
      !data[0]?.data ||
      !data[2]?.data ||
      data[0].data.length === 0 ||
      data[2].data.length === 0
    ) {
      console.warn('Invalid data structure for SurfHeightGraphTravel');
      return undefined;
    }

    const svg = select(svgRef.current);
    svg.selectAll('*').remove(); // Clear previous content

    // Also clean up any existing overlays
    select(containerRef.current).selectAll('.overlay-div').remove();

    const margin = { top: 20, right: 0, bottom: 20, left: 0 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    // Create scales with safe array access
    const xScale = scaleLinear()
      .domain([0, max(data[0].data, (d: { x: number }) => d.x) || 12])
      .range([0, width]);

    // Safe access to data[2] with additional validation
    const maxYValue =
      data[2].data.length > 0 ? Math.max(...data[2].data.map((item: { y: number }) => item.y)) : 0;
    const yScale = scaleLinear()
      .domain([0, Math.ceil(maxYValue)])
      .range([height, 0]);

    // Create line generator
    const lineGenerator = line<{ x: number; y: number }>()
      .x((d: { x: number }) => xScale(d.x))
      .y((d: { y: number }) => yScale(d.y))
      .curve(curveLinear);

    // Add grid lines
    const gridGroup = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Horizontal grid lines
    const yTicks = yScale.ticks(6);
    gridGroup
      .selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', width)
      .attr('y1', (d: number) => yScale(d))
      .attr('y2', (d: number) => yScale(d))
      .attr('stroke', '#dddddd')
      .attr('stroke-width', 1)
      .attr('opacity', 1);

    // Add axis labels
    const axisGroup = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    axisGroup
      .selectAll('.axis-label')
      .data(yTicks)
      .enter()
      .append('g')
      .attr('class', 'axis-label')
      .attr('transform', (d: number) => `translate(0, ${yScale(d)})`)
      .each(function (this: any) {
        const g = select(this);
        g.append('line')
          .attr('x1', 0)
          .attr('x2', 0)
          .attr('y1', 0)
          .attr('y2', 0)
          .attr('stroke', 'transparent')
          .attr('stroke-width', 1);

        g.append('text')
          .attr('dominant-baseline', 'central')
          .attr('text-anchor', 'end')
          .attr('transform', 'translate(-5, 0) rotate(0)')
          .style('fill', '#98A2AF')
          .style('font-size', '14px')
          .style('font-weight', '600')
          .style('font-family', 'sans-serif')
          .text((d: unknown) =>
            (d as number) % 1 === 0 ? `${d}${surfHeightUnit === 'M' ? 'm' : 'ft'}` : '',
          );
      });

    // Add lines for each data series
    const chartGroup = svg
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    chartGroup
      .selectAll('.line')
      .data(data)
      .enter()
      .append('path')
      .attr('class', 'line')
      .attr('d', (d: Data) => lineGenerator(d.data) || '')
      .attr('fill', 'none')
      .attr('stroke', (d: Data) => d.color || 'rgba(49, 78, 230, 1)')
      .attr('stroke-width', 2);

    // Add legend
    const legendGroup = svg
      .append('g')
      .attr('transform', `translate(${width - 213}, ${margin.top - 20})`);

    legendGroup
      .selectAll('.legend-item')
      .data(data)
      .enter()
      .append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d: Data, i: number) => `translate(${i * 81}, 0)`)
      .each(function (this: any, seriesData: Data) {
        const g = select(this);

        g.append('rect')
          .attr('width', 81)
          .attr('height', 10)
          .attr('fill', 'transparent')
          .style('cursor', 'auto')
          .style('pointer-events', 'none');

        g.append('circle')
          .attr('r', 5)
          .attr('cx', 5)
          .attr('cy', 5)
          .attr('fill', seriesData.color || 'rgba(49, 78, 230, 1)')
          .attr('opacity', 1)
          .attr('stroke-width', 0)
          .attr('stroke', 'transparent')
          .style('pointer-events', 'none');

        g.append('text')
          .attr('text-anchor', 'start')
          .attr('x', 18)
          .attr('y', 5)
          .style('fill', '#171717')
          .style('font-family', 'Linear Sans')
          .style('font-size', '14px')
          .style('font-weight', '600')
          .style('dominant-baseline', 'central')
          .style('pointer-events', 'none')
          .style('user-select', 'none')
          .text(seriesData.id);
      });

    // Create DOM overlay element outside SVG
    select(containerRef.current)
      .append('div')
      .attr('class', 'overlay-div')
      .style('position', 'absolute')
      .style('top', '40px') // Account for heading
      .style('left', '0px')
      .style('width', `${dimensions.width}px`)
      .style('height', `${dimensions.height}px`)
      .style('background', 'transparent')
      .style('pointer-events', 'all')
      .style('cursor', 'pointer')
      .style('z-index', 1000)
      .on('mousemove', (event: MouseEvent) => {
        // Remove any existing tooltips first
        selectAll('.tooltip').remove();

        // Get mouse position relative to the overlay
        const rect = (event.target as Element).getBoundingClientRect();
        const mouseX = event.clientX - rect.left - margin.left;

        // More robust data validation before accessing arrays
        if (!data[0]?.data || data[0].data.length === 0) return;

        const xValue = xScale.invert(mouseX);
        const closestIndex = data[0].data.reduce(
          (closestIdx, point, idx) =>
            Math.abs(point.x - xValue) < Math.abs(data[0].data[closestIdx].x - xValue)
              ? idx
              : closestIdx,
          0,
        );

        const slice: Slice = {
          points: data
            .filter((series) => series?.data && closestIndex < series.data.length)
            .map((series) => ({
              activeIndex: closestIndex,
              id: `${series.id}-${closestIndex}`,
              mobile: false,
              data: {
                x: series.data[closestIndex].x,
                y: series.data[closestIndex].y,
                yFormatted: Math.round(series.data[closestIndex].y),
              },
              serieId: series.id,
            })),
        };

        const tooltip = select('body')
          .append('div')
          .attr('class', 'tooltip')
          .style('position', 'absolute')
          .style('background', 'white')
          .style('border-radius', '4px')
          .style('box-shadow', '0px 5px 15px rgba(0, 0, 0, 0.1)')
          .style('padding', '7px 9px')
          .style('pointer-events', 'none')
          .style('z-index', 1000)
          .style('opacity', 0);

        slice.points.forEach((point) => {
          tooltip
            .append('div')
            .style('font-size', '12px')
            .style('font-weight', '600')
            .style('color', '#171717')
            .html(
              `<span style="padding-right: 40px; font-size: 12px;">${point.serieId}</span>${
                point.data.yFormatted
              }${surfHeightUnit === 'M' ? 'm' : 'ft'}`,
            );
        });

        tooltip
          .style('left', `${event.pageX + 10}px`)
          .style('top', `${event.pageY - 10}px`)
          .style('opacity', 1);
      })
      .on('mouseout', () => {
        selectAll('.tooltip').remove();
      });

    // Cleanup function to remove tooltips on component unmount
    return () => {
      selectAll('.tooltip').remove();
      select(containerRef.current).selectAll('.overlay-div').remove();
    };
  }, [data, mobile, surfHeightUnit, dimensions]);

  // Safe access to activeMonth data with bounds checking
  const isValidActiveIndex =
    activeIndex >= 0 &&
    data?.length >= 3 &&
    data[0]?.data?.length > activeIndex &&
    data[2]?.data?.length > activeIndex;

  const activeMonth = {
    min: isValidActiveIndex ? Math.round(data[0].data[activeIndex].y) : 0,
    max: isValidActiveIndex ? Math.round(data[2].data[activeIndex].y) : 0,
  };
  const unit = surfHeightUnit === 'M' ? 'm' : 'ft';

  // Early return if data is invalid
  if (!data || data.length < 3 || !data[0]?.data || !data[2]?.data) {
    return (
      <div ref={containerRef} className={Styles.surfHeightGraph}>
        <TravelGraphsHeading heading="Surf Height" />
        <p>No data available</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={Styles.surfHeightGraph}>
      <TravelGraphsHeading heading="Surf Height" />
      {mobile ? (
        <h4>
          {`${activeMonth.min}-${activeMonth.max}`}
          <span>{unit}</span>
        </h4>
      ) : (
        <>
          <svg
            ref={svgRef}
            width={dimensions.width}
            height={dimensions.height}
            role="img"
            aria-label="Surf height graph showing minimum, average, and maximum wave heights per month"
            style={{ width: '100%', height: 'auto' }}
          >
            <rect
              width={dimensions.width}
              height={dimensions.height}
              fill="transparent"
              style={{ pointerEvents: 'none' }}
            />
          </svg>
          <div
            ref={tooltipRef}
            style={{
              display: 'none',
              position: 'absolute',
              pointerEvents: 'none',
            }}
          />
        </>
      )}
    </div>
  );
};

export default SurfHeightGraphTravel;
